import { inject, Injectable } from '@angular/core';

import { CellValue, Row, Workbook, Worksheet } from 'exceljs';

import { SURVEY_DEFAULT_LANGUAGE_CODE, Translation, TranslationEntry } from '@empathy/survey-core';

import { SurveyEditorService } from './survey-editor.service';
import { TranslationColumns } from '../models/translations/translations-import-export.contants';
import { TranslationsImportIssueType } from '../models/translations/translations-import-issue-type.enum';
import { TranslationsImportIssue } from '../models/translations/translations-import-issue.interface';
import { TranslationsImportResult } from '../models/translations/translations-import-result.interface';

@Injectable({ providedIn: 'root' })
export class ImportTranslationsService {
  private surveyEditorService = inject(SurveyEditorService);
  private defaultLanguageCode$ = inject(SURVEY_DEFAULT_LANGUAGE_CODE);

  async import(file: File, translation: Translation, overwriteExisting: boolean): Promise<TranslationsImportResult> {
    const issues: TranslationsImportIssue[] = [];

    const worksheet = await this.loadWorksheet(file, issues);

    if (!worksheet) {
      return { entries: [], issues };
    }

    const validationIssues = this.validateWorksheet(worksheet, translation.code);

    if (validationIssues.length) {
      return { entries: [], issues: [...issues, ...validationIssues] };
    }

    const { entries, rowIssues } = this.processRows(worksheet, translation, overwriteExisting);

    return {
      entries: entries,
      issues: [...issues, ...rowIssues]
    };
  }

  private async loadWorksheet(file: File, issues: TranslationsImportIssue[]): Promise<Worksheet | undefined> {
    try {
      const workbook = new Workbook();
      const buffer = await file.arrayBuffer();
      await workbook.xlsx.load(buffer);
      return workbook.worksheets?.[0];
    } catch (e: unknown) {
      issues.push({
        type: TranslationsImportIssueType.Error,
        message: 'The file could not be processed.'
      });
      return undefined;
    }
  }

  private validateWorksheet(worksheet: Worksheet, languageCode: string): TranslationsImportIssue[] {
    const issues: TranslationsImportIssue[] = [];
    const headerRow = worksheet.getRow(1);

    if (!this.hasRequiredColumns(headerRow, languageCode)) {
      issues.push({
        type: TranslationsImportIssueType.Error,
        message: `Incorrect format - The worksheet must have a header row with following columns: ID, Ignore, ${this.defaultLanguageCode$()}, ${languageCode}`
      });
      return issues;
    }

    const localizedHeader = headerRow.getCell(TranslationColumns.localizedLanguage).value;

    if (localizedHeader !== languageCode) {
      issues.push({
        type: TranslationsImportIssueType.Error,
        message: `Language mismatch - worksheet must have country code matching the current language: ${languageCode}`
      });
    }

    return issues;
  }

  private processRows(
    worksheet: Worksheet,
    translation: Translation,
    overwriteExisting: boolean
  ): { entries: TranslationEntry[]; rowIssues: TranslationsImportIssue[] } {
    const rowIssues: TranslationsImportIssue[] = [];
    const translationKeys = new Set(translation.entries.map((entry) => entry.key));
    const translationValues = new Map(translation.entries.map((entry) => [entry.key, entry.value]));
    const questionCodeMap = new Map(this.surveyEditorService.surveyEditor$().questions.map((question) => [question.code, question.id]));

    const entries: TranslationEntry[] = [];
    const importedKeys = new Set<string>();

    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber === 1) {
        return;
      }

      const importedId = this.getImportedId(row);

      if (!importedId) {
        rowIssues.push(this.createImportWarning(rowNumber, 'Missing ID value'));
        return;
      }

      const key = this.toEntryKey(importedId, translationKeys, questionCodeMap);

      if (importedKeys.has(key)) {
        rowIssues.push(this.createImportWarning(rowNumber, `Duplicate ID "${importedId}" - each ID must be unique`));
        return;
      }

      if (!translationKeys.has(key)) {
        rowIssues.push(this.createImportWarning(rowNumber, `Invalid ID "${importedId}" - no matching translation ID`));
        return;
      }

      const translationValue = translationValues.get(key);

      importedKeys.add(key);

      if (!overwriteExisting && translationValue != null && translationValue !== '') {
        return;
      }

      entries.push(this.createEntry(row, key));
    });

    return { entries, rowIssues };
  }

  private hasRequiredColumns(headerRow: Row, localizedLanguageCode: string): boolean {
    const idCol = headerRow.getCell(TranslationColumns.id).value;
    const ignoreCol = headerRow.getCell(TranslationColumns.ignore).value;
    const defaultCol = headerRow.getCell(TranslationColumns.defaultLanguage).value;
    const localizedtCol = headerRow.getCell(TranslationColumns.localizedLanguage).value;

    return idCol === 'ID' && ignoreCol === 'Ignore' && defaultCol === this.defaultLanguageCode$() && localizedtCol == localizedLanguageCode;
  }

  private getImportedId(row: Row): string {
    const id = row.getCell(TranslationColumns.id).value;
    return id == null ? '' : String(id).trim();
  }

  private toEntryKey(importedId: string, translationKeys: Set<string>, questionCodeMap: Map<string, string>): string | null {
    if (translationKeys.has(importedId)) {
      return importedId;
    }

    if (!importedId.includes('.')) {
      return null;
    }

    const [questionCode] = importedId.split('.');
    const questionId = questionCodeMap.get(questionCode);
    if (!questionId) {
      return null;
    }

    const key = importedId.replace(questionCode, questionId);
    return translationKeys.has(key) ? key : null;
  }

  private createEntry(row: Row, key: string): TranslationEntry {
    return {
      key,
      value: String(row.getCell(TranslationColumns.localizedLanguage).value ?? ''),
      ignoredInd: this.toBoolean(row.getCell(TranslationColumns.ignore).value),
      outdatedInd: false
    };
  }

  private createImportWarning(row: number, message: string): TranslationsImportIssue {
    return {
      type: TranslationsImportIssueType.Warning,
      row,
      message
    };
  }

  private toBoolean(value: CellValue): boolean {
    return String(value).trim().toLowerCase() === 'true';
  }
}
